import { useState } from "react";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import { Button, Table, Tag, Modal, Form, Input, Select, DatePicker, Popconfirm, message, Tabs, Tooltip } from "antd";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import type { ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import {
  useDbsList, useDbsCreate, useDbsUpdate, useDbsDelete,
  useRtwList, useRtwCreate, useRtwUpdate, useRtwDelete,
} from "../../../hooks/api/compliance/hooks";
import { IDbsCheck, IDbsCreatePayload, IRtwCheck, IRtwCreatePayload } from "../../../hooks/api/compliance/callbacks";
import { useFieldWorkerList } from "../../../hooks/api/field-workers/hooks";

const DBS_LEVEL_OPTS = [
  { value: "basic", label: "Basic" },
  { value: "standard", label: "Standard" },
  { value: "enhanced", label: "Enhanced" },
  { value: "enhanced_barred", label: "Enhanced + Barred" },
];

const DBS_STATUS_OPTS = [
  { value: "pending", label: "Pending" },
  { value: "clear", label: "Clear" },
  { value: "has_information", label: "Has Information" },
];

const RTW_DOC_OPTS = [
  { value: "passport", label: "Passport (UK/Foreign)" },
  { value: "brp", label: "Biometric Residence Permit (BRP)" },
  { value: "share_code", label: "Share Code (Home Office)" },
  { value: "birth_cert_ni", label: "Birth Certificate + NI" },
  { value: "eu_settlement", label: "EU Settlement Status" },
];

const RTW_STATUS_OPTS = [
  { value: "pending", label: "Pending" },
  { value: "pass", label: "Pass" },
  { value: "fail", label: "Fail" },
];

const STATUS_COLOR: Record<string, string> = {
  pending: "orange",
  clear: "green",
  has_information: "volcano",
  pass: "green",
  fail: "red",
};

function workerOptions(workers: any[]) {
  return workers.map((w: any) => ({
    value: w.id,
    label: [w.first_name, w.last_name].filter(Boolean).join(" ") || w.email,
  }));
}

// ── DBS Checks Tab ────────────────────────────────────────────────────────────

function DbsModal({ open, editing, workers, onClose, onSuccess }: {
  open: boolean; editing: IDbsCheck | null; workers: any[]; onClose: () => void; onSuccess: () => void;
}) {
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const isEdit = !!editing;

  const create = useDbsCreate({ onSuccess: () => { msgApi.success("DBS check added."); onSuccess(); }, onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed") });
  const update = useDbsUpdate({ onSuccess: () => { msgApi.success("DBS check updated."); onSuccess(); }, onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed") });

  const handleOpen = (v: boolean) => {
    if (!v) return;
    if (isEdit && editing) {
      form.setFieldsValue({
        ...editing,
        issue_date: editing.issue_date ? dayjs(editing.issue_date) : null,
        expiry_date: editing.expiry_date ? dayjs(editing.expiry_date) : null,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ status: "pending" });
    }
  };

  const handleSubmit = () => {
    form.validateFields().then((vals) => {
      const payload = {
        ...vals,
        issue_date: vals.issue_date ? vals.issue_date.format("YYYY-MM-DD") : null,
        expiry_date: vals.expiry_date ? vals.expiry_date.format("YYYY-MM-DD") : null,
      };
      if (isEdit && editing) update.mutate({ ...payload, id: editing.id });
      else create.mutate(payload as IDbsCreatePayload);
    });
  };

  return (
    <Modal open={open} onCancel={onClose} afterOpenChange={handleOpen} title={isEdit ? "Edit DBS Check" : "Add DBS Check"} footer={null} destroyOnClose width={480}>
      {ctx}
      <Form form={form} layout="vertical" size="small" className="mt-3">
        <Form.Item name="user_id" label="Worker" rules={[{ required: true, message: "Worker required" }]}>
          <Select showSearch placeholder="Select worker…" filterOption={(v, o) => (o?.label as string)?.toLowerCase().includes(v.toLowerCase())} options={workerOptions(workers)} disabled={isEdit} />
        </Form.Item>
        <Form.Item name="check_level" label="Check Level" rules={[{ required: true, message: "Check level required" }]}>
          <Select options={DBS_LEVEL_OPTS} placeholder="Select level…" />
        </Form.Item>
        <Form.Item name="certificate_number" label="Certificate Number">
          <Input placeholder="DBS certificate number…" />
        </Form.Item>
        <div className="grid grid-cols-2 gap-x-4">
          {/* issue_date is NOT NULL on dbs_checks with no default — leaving
              this optional let a submit with it blank reach the API and
              crash with a raw SQL "cannot be null" error instead of this
              inline validation message. */}
          <Form.Item name="issue_date" label="Issue Date" rules={[{ required: true, message: "Issue date required" }]}>
            <DatePicker className="w-full" format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="expiry_date" label="Expiry Date">
            <DatePicker className="w-full" format="DD/MM/YYYY" />
          </Form.Item>
        </div>
        <Form.Item name="status" label="Status" initialValue="pending">
          <Select options={DBS_STATUS_OPTS} />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} />
        </Form.Item>
        <div className="flex justify-end gap-2 mt-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={create.isPending || update.isPending} onClick={handleSubmit}>
            {isEdit ? "Save" : "Add DBS Check"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

function DbsTab({ workers }: { workers: any[] }) {
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<IDbsCheck | null>(null);
  const [msgApi, ctx] = message.useMessage();

  const { data, isLoading, refetchWithQuery } = useDbsList({ page, limit: 20 });
  const items: IDbsCheck[] = (data?.data ?? []) as IDbsCheck[];
  const total = Number((data as any)?.extra?.total ?? 0);

  const del = useDbsDelete({
    onSuccess: () => { msgApi.success("Deleted."); refetchWithQuery({ page }); },
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  const columns: ColumnType<IDbsCheck>[] = [
    { title: "Worker", dataIndex: "worker_name", key: "worker_name", render: (v) => <span className="font-medium text-slate-800">{v ?? "—"}</span> },
    { title: "Level", dataIndex: "check_level", key: "check_level", render: (v) => <Tag color="blue">{(v ?? "").replace(/_/g, " ")}</Tag> },
    { title: "Certificate #", dataIndex: "certificate_number", key: "certificate_number", render: (v) => v ?? "—" },
    { title: "Issue Date", dataIndex: "issue_date", key: "issue_date", render: (v) => v ? dayjs(v).format("DD MMM YYYY") : "—" },
    {
      title: "Expiry", dataIndex: "expiry_date", key: "expiry_date",
      render: (v) => {
        if (!v) return "—";
        const expired = dayjs(v).isBefore(dayjs(), "day");
        const soon = !expired && dayjs(v).diff(dayjs(), "day") < 60;
        return <span className={expired ? "text-red-600 font-medium" : soon ? "text-amber-600 font-medium" : ""}>{dayjs(v).format("DD MMM YYYY")}</span>;
      },
    },
    { title: "Status", dataIndex: "status", key: "status", render: (v) => <Tag color={STATUS_COLOR[v] ?? "default"}>{v}</Tag> },
    {
      title: "Actions", key: "actions",
      render: (_, r) => (
        <div className="flex gap-1">
          <Button size="small" icon={<Pencil size={11} />} onClick={() => { setEditing(r); setModalOpen(true); }} />
          <Popconfirm title="Delete DBS check?" onConfirm={() => del.mutate(r.id)} okButtonProps={{ danger: true }}>
            <Button size="small" icon={<Trash2 size={11} />} danger type="text" />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <>
      {ctx}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-slate-600">{total} DBS checks on record</span>
        <ButtonWrapper type="primary" icon={<Plus size={14} />} onClick={() => { setEditing(null); setModalOpen(true); }} className="flex items-center gap-2">
          Add DBS Check
        </ButtonWrapper>
      </div>
      <Table dataSource={items} columns={columns} rowKey="id" loading={isLoading}
        pagination={{ current: page, pageSize: 20, total, showSizeChanger: false, onChange: (p) => { setPage(p); refetchWithQuery({ page: p }); } }}
        scroll={{ x: 700 }} />
      <DbsModal open={modalOpen} editing={editing} workers={workers} onClose={() => { setModalOpen(false); setEditing(null); }} onSuccess={() => { setModalOpen(false); setEditing(null); refetchWithQuery({ page }); }} />
    </>
  );
}

// ── Right to Work Tab ─────────────────────────────────────────────────────────

function RtwModal({ open, editing, workers, onClose, onSuccess }: {
  open: boolean; editing: IRtwCheck | null; workers: any[]; onClose: () => void; onSuccess: () => void;
}) {
  const [form] = Form.useForm();
  const [msgApi, ctx] = message.useMessage();
  const isEdit = !!editing;

  const create = useRtwCreate({ onSuccess: () => { msgApi.success("RTW check added."); onSuccess(); }, onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed") });
  const update = useRtwUpdate({ onSuccess: () => { msgApi.success("RTW check updated."); onSuccess(); }, onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed") });

  const handleOpen = (v: boolean) => {
    if (!v) return;
    if (isEdit && editing) {
      form.setFieldsValue({
        ...editing,
        check_date: editing.check_date ? dayjs(editing.check_date) : null,
        expiry_date: editing.expiry_date ? dayjs(editing.expiry_date) : null,
        follow_up_date: editing.follow_up_date ? dayjs(editing.follow_up_date) : null,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ status: "pending", check_date: dayjs() });
    }
  };

  const handleSubmit = () => {
    form.validateFields().then((vals) => {
      const payload = {
        ...vals,
        check_date: vals.check_date ? vals.check_date.format("YYYY-MM-DD") : null,
        expiry_date: vals.expiry_date ? vals.expiry_date.format("YYYY-MM-DD") : null,
        follow_up_date: vals.follow_up_date ? vals.follow_up_date.format("YYYY-MM-DD") : null,
      };
      if (isEdit && editing) update.mutate({ ...payload, id: editing.id });
      else create.mutate(payload as IRtwCreatePayload);
    });
  };

  return (
    <Modal open={open} onCancel={onClose} afterOpenChange={handleOpen} title={isEdit ? "Edit RTW Check" : "Add Right to Work Check"} footer={null} destroyOnClose width={480}>
      {ctx}
      <Form form={form} layout="vertical" size="small" className="mt-3">
        <Form.Item name="user_id" label="Worker" rules={[{ required: true, message: "Worker required" }]}>
          <Select showSearch placeholder="Select worker…" filterOption={(v, o) => (o?.label as string)?.toLowerCase().includes(v.toLowerCase())} options={workerOptions(workers)} disabled={isEdit} />
        </Form.Item>
        <Form.Item name="document_type" label="Document Type" rules={[{ required: true, message: "Document type required" }]}>
          <Select options={RTW_DOC_OPTS} placeholder="Select document type…" />
        </Form.Item>
        <Form.Item name="document_number" label="Document Number">
          <Input placeholder="Passport / permit number…" />
        </Form.Item>
        <Form.Item name="share_code" label="Share Code">
          <Input placeholder="HMRC share code (if applicable)…" />
        </Form.Item>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="check_date" label="Check Date">
            <DatePicker className="w-full" format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="expiry_date" label="Expiry Date">
            <DatePicker className="w-full" format="DD/MM/YYYY" />
          </Form.Item>
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item name="status" label="Status" initialValue="pending">
            <Select options={RTW_STATUS_OPTS} />
          </Form.Item>
          <Form.Item name="follow_up_date" label="Follow-Up Date">
            <DatePicker className="w-full" format="DD/MM/YYYY" />
          </Form.Item>
        </div>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} />
        </Form.Item>
        <div className="flex justify-end gap-2 mt-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={create.isPending || update.isPending} onClick={handleSubmit}>
            {isEdit ? "Save" : "Add RTW Check"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

function RtwTab({ workers }: { workers: any[] }) {
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<IRtwCheck | null>(null);
  const [msgApi, ctx] = message.useMessage();

  const { data, isLoading, refetchWithQuery } = useRtwList({ page, limit: 20 });
  const items: IRtwCheck[] = (data?.data ?? []) as IRtwCheck[];
  const total = Number((data as any)?.extra?.total ?? 0);

  const del = useRtwDelete({
    onSuccess: () => { msgApi.success("Deleted."); refetchWithQuery({ page }); },
    onError: (e: any) => msgApi.error(e?.response?.data?.message || "Failed"),
  });

  const columns: ColumnType<IRtwCheck>[] = [
    { title: "Worker", dataIndex: "worker_name", key: "worker_name", render: (v) => <span className="font-medium text-slate-800">{v ?? "—"}</span> },
    { title: "Document Type", dataIndex: "document_type", key: "document_type", render: (v) => <Tag color="purple">{(v ?? "").replace(/_/g, " ")}</Tag> },
    { title: "Document #", dataIndex: "document_number", key: "document_number", render: (v) => v ?? "—" },
    { title: "Checked", dataIndex: "check_date", key: "check_date", render: (v) => v ? dayjs(v).format("DD MMM YYYY") : "—" },
    {
      title: "Expiry", dataIndex: "expiry_date", key: "expiry_date",
      render: (v) => {
        if (!v) return "—";
        const expired = dayjs(v).isBefore(dayjs(), "day");
        const soon = !expired && dayjs(v).diff(dayjs(), "day") < 60;
        return <span className={expired ? "text-red-600 font-medium" : soon ? "text-amber-600 font-medium" : ""}>{dayjs(v).format("DD MMM YYYY")}</span>;
      },
    },
    { title: "Status", dataIndex: "status", key: "status", render: (v) => <Tag color={STATUS_COLOR[v] ?? "default"}>{v}</Tag> },
    {
      title: "Actions", key: "actions",
      render: (_, r) => (
        <div className="flex gap-1">
          <Button size="small" icon={<Pencil size={11} />} onClick={() => { setEditing(r); setModalOpen(true); }} />
          <Popconfirm title="Delete RTW check?" onConfirm={() => del.mutate(r.id)} okButtonProps={{ danger: true }}>
            <Button size="small" icon={<Trash2 size={11} />} danger type="text" />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <>
      {ctx}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-slate-600">{total} right to work checks on record</span>
        <ButtonWrapper type="primary" icon={<Plus size={14} />} onClick={() => { setEditing(null); setModalOpen(true); }} className="flex items-center gap-2">
          Add RTW Check
        </ButtonWrapper>
      </div>
      <Table dataSource={items} columns={columns} rowKey="id" loading={isLoading}
        pagination={{ current: page, pageSize: 20, total, showSizeChanger: false, onChange: (p) => { setPage(p); refetchWithQuery({ page: p }); } }}
        scroll={{ x: 700 }} />
      <RtwModal open={modalOpen} editing={editing} workers={workers} onClose={() => { setModalOpen(false); setEditing(null); }} onSuccess={() => { setModalOpen(false); setEditing(null); refetchWithQuery({ page }); }} />
    </>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const CompliancePage = () => {
  const { data: workerRes } = useFieldWorkerList({ page: 1, limit: 200 });
  const workers = (workerRes?.data ?? []) as any[];

  return (
    <div className="p-6 min-h-screen">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
          <ShieldCheck size={20} style={{ color: "var(--color-primary)" }} />
          Compliance
        </h1>
        <p className="text-slate-500 text-sm">Manage DBS checks, right to work verification, and worker compliance records</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
        <Tabs
          className="px-4 pt-2"
          items={[
            { key: "dbs", label: "DBS Checks", children: <div className="pb-4 px-2"><DbsTab workers={workers} /></div> },
            { key: "rtw", label: "Right to Work", children: <div className="pb-4 px-2"><RtwTab workers={workers} /></div> },
          ]}
        />
      </div>
    </div>
  );
};

export default CompliancePage;
